/**
 * Informes adjuntos de ejecuciones antiguas
 * (`/api/informes-adjuntos`).
 *
 * Documentos externos que solo se agregan y se consultan: por diseño no hay
 * edición ni borrado.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type InformeAdjunto from '@/interfaces/InformeAdjunto'
import { http, getAuthToken } from '@/services/http'

const API_BASE = `${process.env.VUE_APP_API_BASE_URL}/api`

/**
 * Lista informes adjuntos · `GET /api/ejecuciones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns InformeAdjunto[]
 */
export async function listarInformesAdjuntosService(
  idEjecucion: number
): Promise<InformeAdjunto[]> {
  const result = await http.get<InformeAdjunto[]>(`/api/ejecuciones/${idEjecucion}/informes-adjuntos`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Sube varios archivos de una vez. FormData no pasa por http.ts (multipart no es
 * JSON), así que se inyecta el token manualmente, igual que en FichasService.
 */
export async function subirInformesAdjuntosService(
  idEjecucion: number,
  archivos: File[]
): Promise<{ ok: boolean; adjuntos?: InformeAdjunto[]; error?: string }> {
  try {
    const formData = new FormData()
    archivos.forEach(a => formData.append('archivos[]', a))

    const token = getAuthToken()
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const endpoint = `${API_BASE}/ejecuciones/${idEjecucion}/informes-adjuntos`
    const res = await fetch(endpoint, { method: 'POST', headers, body: formData })
    const body = await res.json().catch(() => ({}))

    if (!res.ok) {
      // 422 devuelve errores por campo (archivos.0, archivos.1…); se muestra el primero.
      const errores = (body as any).errors as Record<string, string[]> | undefined
      const primero = errores ? Object.values(errores).flat()[0] : undefined
      return { ok: false, error: primero ?? (body as any).message ?? `Error HTTP ${res.status}` }
    }

    return { ok: true, adjuntos: (body as any).adjuntos ?? [] }
  } catch (err) {
    console.error('Error al subir informes adjuntos:', err)
    return { ok: false, error: 'Error de red al subir los archivos' }
  }
}
